import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Toolbar,
  Tooltip
} from '@mui/material';
import { StatCard, cardGradients } from '../components/StatCard';
import {
  Check,
  Close,
  ContentCopy,
  OpenInNew,
  AttachMoney,
  Block,
  LockOpen,
  Verified
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import UserDetailsModal from '../components/UserDetailsModal';
import { formatNumber, formatCurrency } from '../utils/formatters';

// §22.09.2026 (владелец: «галочки с телефона очень мелкие, вообще не попасть»).
// Кнопки действий были россыпью разного размера: часть иконок шла с
// fontSize="small" (20px), часть без него (24px), а сетка 2×N на узком экране
// рвала их по строкам. Один общий размер для всех: квадрат 40×40 (на телефоне
// 44×44 — минимум для пальца по гайдлайнам), рамка, одинаковая иконка внутри.
const ACTION_BTN_SX = {
  width: 40,
  height: 40,
  fontSize: 18, // для эмодзи-кнопки (🔈/🔇)
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '10px',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '@media (max-width:600px)': {
    width: 44,
    height: 44,
    '& .MuiSvgIcon-root': { fontSize: 22 },
  },
};

// Форматирование чисел в СНГ формате
const fmtNum = (num, dec = 0) => {
  if (num === null || num === undefined) return '0';
  return parseFloat(num).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: dec });
};

const Withdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [detailsDialog, setDetailsDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selected, setSelected] = useState([]);
  const [blacklistDialog, setBlacklistDialog] = useState(null);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [whitelistDialog, setWhitelistDialog] = useState(null);
  const [whitelistReason, setWhitelistReason] = useState('');
  const [autoSperm, setAutoSperm] = useState(false);
  const [autoUsdt, setAutoUsdt] = useState(false);
  const [processingWithdrawals, setProcessingWithdrawals] = useState([]); // ID выводов в обработке
  const [walletBalance, setWalletBalance] = useState(null); // Баланс системного кошелька
  // Настройки лимитов контроля выводов
  const [withdrawalLimits, setWithdrawalLimits] = useState({
    auto_approve_max_amount: 200,
    daily_limit_for_review: 500,
    // ⚡ §06.09.2026 (владелец): мгновенный вывод USDT из казино — тумблер и порог.
    // Тумблер в БД лежит числом 1/0 (эндпоинт лимитов пишет только value_number).
    slots_instant_enabled: true,
    slots_instant_max: 10,
    slots_instant_daily: 20
  });
  const [limitsExpanded, setLimitsExpanded] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    pendingAmount: 0,
    approved: 0,
    approvedAmount: 0,
    rejected: 0,
    rejectedAmount: 0,
    processing: 0,
    processingAmount: 0
  });

  // Состояние для модального окна пользователя
  const [userDetailsModal, setUserDetailsModal] = useState({ open: false, userId: null });

  // Массовый P&L скан
  const [pnlData, setPnlData] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pnlResults') || '{}'); } catch { return {}; }
  });
  const [pnlScanning, setPnlScanning] = useState(false);
  const [pnlProgress, setPnlProgress] = useState({ current: 0, total: 0 });
  const [pnlModal, setPnlModal] = useState(null);
  const [pnlReportSending, setPnlReportSending] = useState(false);
  const [pnlTaskId, setPnlTaskId] = useState(() => localStorage.getItem('pnlTaskId') || null);

  // Сохраняем результаты в localStorage
  useEffect(() => {
    if (Object.keys(pnlData).length > 0) {
      localStorage.setItem('pnlResults', JSON.stringify(pnlData));
    }
  }, [pnlData]);

  // При загрузке — если есть taskId, сразу забираем результаты
  useEffect(() => {
    const savedTaskId = localStorage.getItem('pnlTaskId');
    if (savedTaskId) {
      setPnlScanning(true);
      adminAPI.get(`/wallet-scanner/pnl-batch-status/${savedTaskId}`)
        .then(resp => {
          if (resp.data.success) {
            setPnlData(resp.data.results || {});
            setPnlProgress({ current: resp.data.progress, total: resp.data.total });
            if (resp.data.status === 'done') {
              setPnlScanning(false);
              setPnlTaskId(null);
              localStorage.removeItem('pnlTaskId');
            }
          }
        })
        .catch(() => { setPnlScanning(false); localStorage.removeItem('pnlTaskId'); });
    }
  }, []);

  // Запуск фонового скана на бэкенде
  const scanAllPnl = async () => {
    // Все уникальные username со страницы (включая игнор)
    const allUsernames = [...new Set(displayWithdrawals.map(w => w.telegram_username).filter(Boolean))];
    const usernames = allUsernames;
    if (usernames.length === 0) return;

    setPnlScanning(true);
    setPnlProgress({ current: 0, total: usernames.length });
    // Старые результаты остаются до обновления
    try {
      const resp = await adminAPI.post('/wallet-scanner/pnl-batch-start', { usernames });
      if (resp.data.success) {
        setPnlTaskId(resp.data.taskId);
        localStorage.setItem('pnlTaskId', resp.data.taskId);
      }
    } catch (e) { setPnlScanning(false); localStorage.removeItem('pnlTaskId'); }
  };

  // Поллинг статуса задачи
  useEffect(() => {
    if (!pnlTaskId) return;
    const interval = setInterval(async () => {
      try {
        const resp = await adminAPI.get(`/wallet-scanner/pnl-batch-status/${pnlTaskId}`);
        if (resp.data.success) {
          setPnlProgress({ current: resp.data.progress, total: resp.data.total });
          // Принудительно новый объект чтобы React перерисовал
          const newResults = resp.data.results || {};
          setPnlData(prev => {
            const merged = { ...prev };
            for (const [k, v] of Object.entries(newResults)) merged[k] = v;
            return { ...merged };
          });
          if (resp.data.status === 'done') {
            setPnlScanning(false);
            setPnlTaskId(null);
            localStorage.removeItem('pnlTaskId');
          }
        }
      } catch (e) {
        setPnlScanning(false);
        setPnlTaskId(null);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [pnlTaskId]);

  const sendPnlReport = async (profitData) => {
    setPnlReportSending(true);
    try {
      await adminAPI.post('/wallet-scanner/profit-report', { profitData });
      alert('Отчёт отправлен!');
    } catch (e) { alert('Ошибка: ' + e.message); }
    setPnlReportSending(false);
  };

  // Мини P&L для строки таблицы
  const getPnlBadge = (username) => {
    const d = pnlData[username];
    if (!d) return null;
    const p = d.profit || {};
    const pr = d.prices || {};
    const sa = p.spermAssets || {};
    const u = p.usdt || {};
    const w = d.wallet || {};
    const tonUsd = pr.tonPriceUsd || 0;
    const spermOnWallet = sa.onWallet || w.spermOnWallet || 0;
    const usdtOut = (u.withdrawn || 0) + (u.balance || 0);
    const usdtIn = u.deposited || 0;
    // Считаем по историческим ценам из monthlyBuys (как полный сканер)
    const mb = d.blockchain?.monthlyBuys || {};
    const usdFromMonthly = Object.values(mb).reduce((s, m) => s + (m.usdSpent || 0), 0);
    const usdInvested = (usdFromMonthly > 0 ? usdFromMonthly : (p.totalTonIn || 0) * tonUsd) + usdtIn;
    const usdFromSells = (d.blockchain?.dexSells || []).reduce((s, sl) => s + (sl.usdReceived || sl.ton * tonUsd), 0);
    const usdSpermOnWallet = spermOnWallet * (pr.spermPriceUsd || 0);
    const usdTonOnWallet = (w.tonOnWallet || p.tonOnWallet || 0) * tonUsd;
    const usdNow = usdFromSells + usdSpermOnWallet + usdTonOnWallet + usdtOut;
    const usdPnl = usdNow - usdInvested;
    return { usdPnl, usdInvested, usdNow, spermOnWallet, tonInvested: p.totalTonIn || 0, data: d };
  };

  useEffect(() => {
    loadWithdrawals();
    loadStats();
    loadAutoApproveMode();
    loadWalletBalance();
    loadWithdrawalLimits();
  }, [page, rowsPerPage, filter]);

  // Автообновление данных каждые 10 секунд
  useEffect(() => {
    const interval = setInterval(() => {
      loadWithdrawals();
      loadStats();
      loadWalletBalance();
    }, 10000); // 10 секунд

    // Очистка интервала при unmount
    return () => clearInterval(interval);
  }, [page, rowsPerPage, filter]); // Зависимости чтобы обновлять с текущими параметрами

  // Загрузка баланса системного кошелька
  const loadWalletBalance = async () => {
    try {
      const response = await adminAPI.getSystemWalletBalance();
      if (response.data.success) {
        setWalletBalance(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки баланса кошелька:', error);
    }
  };

  const loadAutoApproveMode = async () => {
    try {
      const response = await adminAPI.getAutoApproveMode();
      if (response.data.success) {
        setAutoSperm(response.data.sperm || false);
        setAutoUsdt(response.data.usdt || false);
      }
    } catch (error) {
      console.error('Ошибка загрузки режима auto-approve:', error);
    }
  };

  // Загрузка лимитов контроля выводов
  const loadWithdrawalLimits = async () => {
    try {
      const response = await adminAPI.getWithdrawalLimits();
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        setWithdrawalLimits({
          auto_approve_max_amount: d.auto_approve_max_amount || 200,
          daily_limit_for_review: d.daily_limit_for_review || 500,
          // ключа может ещё не быть — тогда считаем включённым (дефолт бэка)
          slots_instant_enabled: d.slots_instant_enabled === undefined
            ? true : Number(d.slots_instant_enabled) > 0,
          slots_instant_max: d.slots_instant_max === undefined ? 10 : Number(d.slots_instant_max),
          slots_instant_daily: d.slots_instant_daily === undefined ? 20 : Number(d.slots_instant_daily)
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки лимитов выводов:', error);
    }
  };

  // Сохранение лимитов контроля выводов
  const saveWithdrawalLimits = async () => {
    setSavingLimits(true);
    try {
      await adminAPI.setWithdrawalLimits(withdrawalLimits);
      alert('Лимиты сохранены!');
    } catch (error) {
      console.error('Ошибка сохранения лимитов:', error);
      alert('Ошибка сохранения лимитов');
    } finally {
      setSavingLimits(false);
    }
  };

  const handleAutoModeChange = async (currency, enabled) => {
    // Текущее состояние обеих валют + меняем нужную
    const next = {
      sperm: currency === 'sperm' ? enabled : autoSperm,
      usdt: currency === 'usdt' ? enabled : autoUsdt,
    };
    try {
      const response = await adminAPI.setAutoApproveMode(next);
      if (response.data.success) {
        setAutoSperm(response.data.sperm);
        setAutoUsdt(response.data.usdt);
      }
    } catch (error) {
      console.error('Ошибка переключения режима:', error);
    }
  };

  const loadWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getWithdrawals({
        page: page + 1,
        limit: rowsPerPage,
        status: filter === 'all' ? undefined : filter
      });

      if (response.data.success) {
        setWithdrawals(response.data.data.withdrawals || []);
      }
    } catch (error) {
      console.error('Ошибка загрузки выводов:', error);
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminAPI.getWithdrawalsStats();
      if (response.data.success) {
        const overall = response.data.data.overall;
        setStats({
          pending: parseInt(overall.pending_count) || 0,
          pendingAmount: parseFloat(overall.pending_amount) || 0,
          processing: parseInt(overall.processing_count) || 0,
          processingAmount: parseFloat(overall.processing_amount) || 0,
          approved: parseInt(overall.approved_count) || 0,
          approvedAmount: parseFloat(overall.approved_amount) || 0,
          rejected: parseInt(overall.rejected_count) || 0,
          rejectedAmount: 0
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Ожидает';
      case 'processing': return 'В обработке';
      case 'approved': return 'Одобрен';
      case 'rejected': return 'Отклонен';
      case 'completed': return 'Выполнен';
      default: return status;
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Выделяем ВСЕ выводы, не только pending
      const newSelected = displayWithdrawals.map(w => w.id);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleApprove = async (withdrawal) => {
    // Добавляем ID в список обрабатываемых
    setProcessingWithdrawals(prev => [...prev, withdrawal.id]);

    try {
      const response = await adminAPI.approveWithdrawal(withdrawal.id);
      if (response.data.success) {
        // Ждем 12 секунд для отправки токенов (10 сек отправка + 2 сек запас)
        await new Promise(resolve => setTimeout(resolve, 12000));

        loadWithdrawals();
        setDetailsDialog(null);
      }
    } catch (error) {
      console.error('Ошибка одобрения вывода:', error);
    } finally {
      // Убираем ID из списка обрабатываемых
      setProcessingWithdrawals(prev => prev.filter(id => id !== withdrawal.id));
    }
  };

  const handleApproveSelected = async () => {
    // Добавляем все выбранные ID в список обрабатываемых
    setProcessingWithdrawals(prev => [...prev, ...selected]);

    try {
      for (const id of selected) {
        await adminAPI.approveWithdrawal(id);
      }

      // Ждем 12 секунд для отправки токенов
      await new Promise(resolve => setTimeout(resolve, 12000));

      setSelected([]);
      loadWithdrawals();
    } catch (error) {
      console.error('Ошибка одобрения выводов:', error);
    } finally {
      // Убираем все обработанные ID
      setProcessingWithdrawals(prev => prev.filter(id => !selected.includes(id)));
    }
  };

  const handleRejectSelected = async () => {
    const reason = prompt('Укажите причину отклонения:');
    if (!reason) return;
    
    try {
      for (const id of selected) {
        await adminAPI.rejectWithdrawal(id, { reason });
      }
      setSelected([]);
      loadWithdrawals();
    } catch (error) {
      console.error('Ошибка отклонения выводов:', error);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;

    try {
      const response = await adminAPI.rejectWithdrawal(rejectDialog.id, {
        reason: rejectReason
      });
      if (response.data.success) {
        loadWithdrawals();
        setRejectDialog(null);
        setRejectReason('');
        setDetailsDialog(null);
      }
    } catch (error) {
      console.error('Ошибка отклонения вывода:', error);
    }
  };

  const handleProcess = async (withdrawal) => {
    try {
      const response = await adminAPI.processWithdrawal(withdrawal.id);
      if (response.data.success) {
        loadWithdrawals();
        setDetailsDialog(null);
      }
    } catch (error) {
      console.error('Ошибка обработки вывода:', error);
    }
  };

  // Добавить в черный список
  const handleBlacklist = async () => {
    if (!blacklistDialog) return;

    try {
      const response = await adminAPI.addToWithdrawalBlacklist(blacklistDialog.id, {
        reason: blacklistReason || 'Без указания причины'
      });
      if (response.data.success) {
        loadWithdrawals();
        setBlacklistDialog(null);
        setBlacklistReason('');
        setDetailsDialog(null);
      }
    } catch (error) {
      console.error('Ошибка добавления в ЧС:', error);
    }
  };

  // Убрать из черного списка
  const handleUnblacklist = async (withdrawal) => {
    if (!window.confirm(`Убрать пользователя ${withdrawal.username || withdrawal.first_name || 'ID:' + withdrawal.user_id} из ЧС?`)) {
      return;
    }

    try {
      const response = await adminAPI.removeFromWithdrawalBlacklist(withdrawal.id);
      if (response.data.success) {
        loadWithdrawals();
        setDetailsDialog(null);
      }
    } catch (error) {
      console.error('Ошибка удаления из ЧС:', error);
    }
  };

  // Добавить в whitelist
  const handleAddToWhitelist = async () => {
    if (!whitelistDialog) return;
    try {
      const response = await adminAPI.addToWithdrawalWhitelist(whitelistDialog.id, {
        reason: whitelistReason || 'Без указания причины'
      });
      if (response.data.success) {
        loadWithdrawals();
        setWhitelistDialog(null);
        setWhitelistReason('');
        setDetailsDialog(null);
      }
    } catch (error) {
      console.error('Ошибка добавления в whitelist:', error);
    }
  };

  // Убрать из whitelist
  const handleRemoveFromWhitelist = async (withdrawal) => {
    if (!window.confirm(`Убрать пользователя ${withdrawal.username || withdrawal.first_name || 'ID:' + withdrawal.user_id} из whitelist?`)) {
      return;
    }
    try {
      const response = await adminAPI.removeFromWithdrawalWhitelist(withdrawal.id);
      if (response.data.success) {
        loadWithdrawals();
        setDetailsDialog(null);
      }
    } catch (error) {
      console.error('Ошибка удаления из whitelist:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const displayWithdrawals = withdrawals;
  const displayStats = stats;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Управление выводами
      </Typography>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Ожидают обработки"
            value={displayStats.pending}
            subtitle={formatCurrency(displayStats.pendingAmount)}
            background={cardGradients.orange}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">
                В обработке
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold' }}>
                {displayStats.processing}
              </Typography>
              <Typography color="white" variant="body2">
                {formatCurrency(displayStats.processingAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">
                Выполнено
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold' }}>
                {displayStats.approved}
              </Typography>
              <Typography color="white" variant="body2">
                {formatCurrency(displayStats.approvedAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">
                Отклонено
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold' }}>
                {displayStats.rejected}
              </Typography>
              <Typography color="white" variant="body2">
                {formatCurrency(displayStats.rejectedAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Предупреждение о низком балансе кошелька */}
      {walletBalance?.has_warnings && walletBalance.warnings.map((warning, index) => (
        <Alert severity="error" sx={{ mb: 2 }} key={index}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            ⚠️ {warning.message}
          </Typography>
          <Typography variant="body2">
            Требуется: <strong>{formatNumber(warning.required, 2)} {warning.token}</strong> |
            Доступно: <strong>{formatNumber(warning.available, 2)} {warning.token}</strong> |
            Не хватает: <strong>{formatNumber(warning.deficit, 2)} {warning.token}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Пополните системный кошелёк для обработки pending выводов. Выводы НЕ отклоняются - они ожидают пополнения.
          </Typography>
        </Alert>
      ))}

      {/* Баланс системного кошелька */}
      {walletBalance && (
        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2">
              💰 <strong>Баланс кошелька:</strong>
            </Typography>
            <Chip
              label={`SPERM: ${formatNumber(walletBalance.balances?.SPERM || 0, 2)}`}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
            <Chip
              label={`USDT: ${formatNumber(walletBalance.balances?.USDT || 0, 2)}`}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #26A17B 0%, #50D69E 100%)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              (обновляется автоматически каждые 10 сек)
            </Typography>
          </Box>
        </Alert>
      )}

      {/* 🔐 Настройки лимитов контроля выводов */}
      <Paper sx={{ p: 2, mb: 2, background: 'rgba(251, 191, 36, 0.05)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, cursor: 'pointer' }} onClick={() => setLimitsExpanded(!limitsExpanded)}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main' }}>
            🔐 Лимиты контроля выводов {limitsExpanded ? '▼' : '▶'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            За 1 раз: ≥{withdrawalLimits.auto_approve_max_amount} SPERM | За сутки: ≥{withdrawalLimits.daily_limit_for_review} SPERM
            {' | '}⚡ Казино: {withdrawalLimits.slots_instant_enabled
              ? `мгновенно до ${withdrawalLimits.slots_instant_max} USDT, ${withdrawalLimits.slots_instant_daily} USDT в сутки`
              : 'выкл'}
          </Typography>
        </Box>
        {limitsExpanded && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Лимит за 1 вывод (SPERM)"
              type="number"
              value={withdrawalLimits.auto_approve_max_amount}
              onChange={(e) => setWithdrawalLimits(prev => ({ ...prev, auto_approve_max_amount: parseFloat(e.target.value) || 0 }))}
              sx={{ width: 180 }}
              helperText="Выводы ≥ этой суммы требуют подтверждения"
            />
            <TextField
              size="small"
              label="Лимит за сутки (SPERM)"
              type="number"
              value={withdrawalLimits.daily_limit_for_review}
              onChange={(e) => setWithdrawalLimits(prev => ({ ...prev, daily_limit_for_review: parseFloat(e.target.value) || 0 }))}
              sx={{ width: 180 }}
              helperText="Если юзер вывел ≥ за сутки, все выводы на ручном"
            />
            {/* ⚡ §06.09.2026 (владелец): вывод ИЗ КАЗИНО до порога уходит в блокчейн
                сразу, без ручного одобрения. Работает независимо от тумблера
                «USDT: Авто/Полу» — тот про все USDT-выводы, этот только про казино. */}
            <FormControlLabel
              control={
                <Switch
                  checked={withdrawalLimits.slots_instant_enabled}
                  onChange={(e) => setWithdrawalLimits(prev => ({ ...prev, slots_instant_enabled: e.target.checked }))}
                  color="success"
                  size="small"
                />
              }
              label={`⚡ Казино мгновенно: ${withdrawalLimits.slots_instant_enabled ? 'вкл' : 'выкл'}`}
              sx={{ mr: 0, height: 40, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <TextField
              size="small"
              label="Порог казино (USDT)"
              type="number"
              value={withdrawalLimits.slots_instant_max}
              onChange={(e) => setWithdrawalLimits(prev => ({ ...prev, slots_instant_max: parseFloat(e.target.value) || 0 }))}
              disabled={!withdrawalLimits.slots_instant_enabled}
              sx={{ width: 180 }}
              helperText="Из слотов до этой суммы — сразу в блокчейн"
            />
            <TextField
              size="small"
              label="Мгновенно в сутки (USDT)"
              type="number"
              value={withdrawalLimits.slots_instant_daily}
              onChange={(e) => setWithdrawalLimits(prev => ({ ...prev, slots_instant_daily: parseFloat(e.target.value) || 0 }))}
              disabled={!withdrawalLimits.slots_instant_enabled}
              sx={{ width: 180 }}
              helperText="Больше — заявка идёт на одобрение"
            />
            <Button
              variant="contained"
              size="small"
              onClick={saveWithdrawalLimits}
              disabled={savingLimits}
              sx={{ height: 40 }}
            >
              {savingLimits ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Предупреждения */}
      {displayStats.pending > 5 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          У вас {displayStats.pending} заявок на вывод ожидают обработки!
          Рекомендуется обработать их как можно скорее.
        </Alert>
      )}

      {/* Таблица выводов */}
      <Paper sx={{ p: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, mr: 'auto' }}>Выводы</Typography>
          <FormControlLabel
            control={<Switch checked={autoSperm} onChange={(e) => handleAutoModeChange('sperm', e.target.checked)} color="primary" size="small" />}
            label={`SPERM: ${autoSperm ? "Авто" : "Полу"}`}
            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.75rem', md: '0.875rem' } } }}
          />
          <FormControlLabel
            control={<Switch checked={autoUsdt} onChange={(e) => handleAutoModeChange('usdt', e.target.checked)} color="secondary" size="small" />}
            label={`USDT: ${autoUsdt ? "Авто" : "Полу"}`}
            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.75rem', md: '0.875rem' } } }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={scanAllPnl}
            disabled={pnlScanning}
            sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, fontSize: '0.7rem', px: 1.5, py: 0.5, minWidth: 'auto' }}
          >
            {pnlScanning ? `${pnlProgress.current}/${pnlProgress.total}` : 'P&L'}
          </Button>
          <FormControl size="small" sx={{ minWidth: { xs: 100, md: 160 } }}>
            <InputLabel>Фильтр по статусу</InputLabel>
            <Select
              value={filter}
              label="Фильтр по статусу"
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">Все заявки</MenuItem>
              <MenuItem value="pending">Ожидают</MenuItem>
              <MenuItem value="processing">В обработке</MenuItem>
              <MenuItem value="approved">Одобрены</MenuItem>
              <MenuItem value="rejected">Отклонены</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {selected.length > 0 && (
          <Toolbar sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ flex: '1 1 100%' }}>
              Выбрано: {selected.length}
            </Typography>
            <Button
              variant="contained"
              color="success"
              onClick={handleApproveSelected}
              sx={{ mr: 1 }}
            >
              Одобрить выбранные
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRejectSelected}
            >
              Отклонить выбранные
            </Button>
          </Toolbar>
        )}

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Пользователь</TableCell>
                <TableCell>Валюта</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell sx={{ minWidth: 360 }}>Источник средств</TableCell>
                <TableCell>Кошелек</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell sx={{ width: 110, maxWidth: 110 }}>Хэш</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell sx={{ minWidth: 152 }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayWithdrawals.map((withdrawal) => {
                const isProcessing = processingWithdrawals.includes(withdrawal.id);
                return (<React.Fragment key={withdrawal.id}>
                <TableRow
                  selected={selected.indexOf(withdrawal.id) !== -1}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      onClick={() => setUserDetailsModal({ open: true, userId: withdrawal.user_id })}
                      sx={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        color: '#6366f1',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#818cf8'
                        }
                      }}
                    >
                      {withdrawal.first_name
                        ? `${withdrawal.first_name}${withdrawal.last_name ? ' ' + withdrawal.last_name : ''}`
                        : (withdrawal.telegram_username || `ID: ${withdrawal.user_id}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {withdrawal.telegram_username ? `@${withdrawal.telegram_username}` : `TG: ${withdrawal.telegram_id}`}
                    </Typography>
                    {withdrawal.telegram_username && (() => {
                      const badge = getPnlBadge(withdrawal.telegram_username);
                      if (!badge) return null;
                      const { usdPnl } = badge;
                      return (
                        <Chip
                          label={`${usdPnl >= 0 ? '+' : ''}$${fmtNum(usdPnl)}`}
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setPnlModal(badge.data); }}
                          sx={{
                            mt: 0.5,
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            bgcolor: usdPnl >= 0 ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)',
                            color: usdPnl >= 0 ? '#4caf50' : '#f44336',
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 }
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={withdrawal.currency || 'SPERM'}
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        background: withdrawal.currency === 'USDT'
                          ? 'linear-gradient(135deg, #26A17B 0%, #50D69E 100%)'
                          : 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {formatNumber(withdrawal.amount, 2)} {withdrawal.currency || 'SPERM'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 360 }}>
                    {withdrawal.funds_source_usdt ? (
                      /* §22.09.2026 (владелец): у USDT-заявки правда СВОЯ. Блок ниже
                         считает SPERM (депозиты, slots_spins, стейк) — напротив
                         долларовой заявки это были цифры из другой валюты. */
                      (() => {
                        const u = withdrawal.funds_source_usdt;
                        const amount = parseFloat(withdrawal.amount) || 0;
                        const win = u.from_profit > 0;
                        const sgn = (n) => (n >= 0 ? '+' : '');
                        return (
                          <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                            <Box sx={{ fontWeight: 'bold', color: win ? '#22c55e' : '#f59e0b' }}>
                              {win
                                ? `Эти ${formatNumber(amount, 2)}: свой депозит ${formatNumber(u.from_own, 2)} + ВЫИГРЫШ ${formatNumber(u.from_profit, 2)}`
                                : `Эти ${formatNumber(amount, 2)}: свой депозит, ВЫИГРЫША НЕТ`}
                            </Box>
                            {u.parts.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.2, rowGap: 0.2, color: '#22c55e' }}>
                                {u.parts.map((p, idx) => (
                                  <Box key={idx} component="span" sx={{ whiteSpace: 'nowrap' }}>
                                    {p.key} <strong>+{formatNumber(p.val, 2)}</strong>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {/* §22.09.2026 (владелец): «вывел» — это когда ОТПРАВЛЕНО в блокчейн.
                                Пока заявка не оплачена, её сумма в «вывел» и в факт-PNL не идёт —
                                для неё отдельная строка «после выплаты станет». */}
                            <Box sx={{ mt: 0.4, pt: 0.4, borderTop: '1px dashed rgba(255,255,255,0.15)', fontSize: '0.68rem', display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.2 }}>
                              <Box component="span" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                {u.scope === 'casino' ? 'Завёл в казино' : 'Завёл в марафон'} {formatNumber(u.deposited, 2)} · Отправлено {formatNumber(u.withdrawn_paid ?? (u.previously_withdrawn + amount), 2)}
                              </Box>
                              <Box component="span" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', color: u.realized_pnl >= 0 ? '#ef4444' : '#22c55e' }}>
                                → PNL сейчас: {sgn(u.realized_pnl)}{formatNumber(u.realized_pnl, 2)} ({u.realized_pnl >= 0 ? 'игрок в ПЛЮСЕ' : 'мы в ПЛЮСЕ'})
                              </Box>
                              {u.paid === false && (
                                <Box component="span" sx={{ whiteSpace: 'nowrap', color: '#f59e0b' }}>
                                  ⏳ ещё НЕ отправлено · после выплаты: {sgn(u.pnl_after)}{formatNumber(u.pnl_after, 2)} ({u.pnl_after >= 0 ? 'игрок в ПЛЮСЕ' : 'мы в ПЛЮСЕ'})
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ mt: 0.4, pt: 0.4, borderTop: '1px dashed rgba(255,255,255,0.15)', color: 'text.secondary', fontSize: '0.68rem', display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.2 }}>
                              {u.ss && (u.ss.wager > 0 || u.ss.modules > 0) ? (
                                <Box component="span" sx={{ whiteSpace: 'nowrap', color: u.ss.net >= 0 ? '#22c55e' : '#ef4444' }}>
                                  Sugar Strike: {sgn(u.ss.net)}{formatNumber(u.ss.net, 2)} (ставил {formatNumber(u.ss.wager, 2)}, выплачено {formatNumber(u.ss.payout, 2)})
                                </Box>
                              ) : u.ss ? (
                                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Sugar Strike: не играл</Box>
                              ) : null}
                              <Box component="span" sx={{ whiteSpace: 'nowrap', color: '#22c55e' }}>
                                Баланс {u.scope === 'casino' ? 'казино' : 'марафона'}: <strong>{formatNumber(u.balance, 2)}</strong>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })()
                    ) : withdrawal.funds_source ? (
                      <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        {(() => {
                          const fs = withdrawal.funds_source;
                          const amount = parseFloat(withdrawal.amount) || 0;
                          const dep = parseFloat(fs.total_deposited) || 0;
                          const prevW = parseFloat(fs.total_previously_withdrawn) || 0;
                          // «Лесенка»: сначала возвращается свой депозит, что выше — выигрыш/доход
                          const ownRemaining = Math.max(0, dep - prevW);
                          const fromOwn = Math.min(amount, ownRemaining);
                          const fromProfit = Math.max(0, amount - fromOwn);
                          // Профит делим по реальным источникам дохода игрока
                          const incomes = [
                            // §03.10.2026: только ЧИСТЫЙ плюс в слотах (выиграл − поставил).
                            // Раньше брались все выигрыши без ставок — у игрока в минусе
                            // почти весь вывод подписывался «выигрышем в слотах».
                            { key: 'Выигрыш в слотах', val: Math.max(0, parseFloat(fs.total_game_balance) || 0), color: '#22c55e', plus: true },
                            { key: 'Стейк', val: parseFloat(fs.total_earned_staking) || 0, color: 'inherit' },
                            { key: 'Лояльность', val: parseFloat(fs.total_loyalty) || 0, color: '#a855f7' },
                            { key: 'Рефералы', val: parseFloat(fs.total_earned_referral) || 0, color: 'inherit' },
                            { key: 'Награды', val: parseFloat(fs.total_tasks) || 0, color: '#06b6d4' },
                            { key: 'Копилка %', val: parseFloat(fs.total_piggybank_interest) || 0, color: 'inherit' },
                          ];
                          const incomeTotal = incomes.reduce((s, i) => s + i.val, 0) || 1;
                          const profitParts = incomes
                            .filter(i => i.val > 0)
                            .map(i => ({ ...i, share: fromProfit * i.val / incomeTotal }))
                            .filter(i => i.share >= 0.5);
                          return (
                            <>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.2, rowGap: 0.2, alignItems: 'baseline' }}>
                                <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  Эти {formatNumber(amount, 0)}:
                                </Box>
                                {fromOwn > 0 && (
                                  <Box component="span" sx={{ color: '#f59e0b', whiteSpace: 'nowrap' }}>
                                    свой депозит <strong>{formatNumber(fromOwn, 0)}</strong>
                                  </Box>
                                )}
                                {profitParts.map((p, idx) => (
                                  <Box key={idx} component="span" sx={{ color: p.color, whiteSpace: 'nowrap' }}>
                                    {p.key} <strong>{p.plus ? '+' : ''}{formatNumber(p.share, 0)}</strong>
                                  </Box>
                                ))}
                                {fromOwn <= 0 && profitParts.length === 0 && (
                                  <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                    нет данных по источникам
                                  </Box>
                                )}
                              </Box>
                              {/* ГЛАВНОЕ: PNL по кэшу. §22.09.2026 (владелец): «вывел» — это
                                  когда ОТПРАВЛЕНО в блокчейн, а не когда подана заявка. */}
                              {(() => {
                                const realized = fs.realized_pnl ?? ((prevW + amount) - dep);
                                const sent = fs.withdrawn_paid ?? (prevW + amount);
                                const sgn = (n) => (n >= 0 ? '+' : '');
                                return (
                                  <Box sx={{ mt: 0.4, pt: 0.4, borderTop: '1px dashed rgba(255,255,255,0.15)', fontSize: '0.68rem', display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.2 }}>
                                    <Box component="span" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                      Внёс {formatNumber(dep, 0)} · Отправлено {formatNumber(sent, 0)}
                                    </Box>
                                    <Box component="span" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', color: realized >= 0 ? '#ef4444' : '#22c55e' }}>
                                      → PNL сейчас: {sgn(realized)}{formatNumber(realized, 0)} ({realized >= 0 ? 'игрок в ПЛЮСЕ' : 'мы в ПЛЮСЕ'})
                                    </Box>
                                    {fs.paid === false && (
                                      <Box component="span" sx={{ whiteSpace: 'nowrap', color: '#f59e0b' }}>
                                        ⏳ ещё НЕ отправлено · после выплаты: {sgn(fs.pnl_after)}{formatNumber(fs.pnl_after, 0)} ({fs.pnl_after >= 0 ? 'игрок в ПЛЮСЕ' : 'мы в ПЛЮСЕ'})
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })()}
                              {/* Контекст: что держит + справочные по движку/стейку */}
                              <Box sx={{ mt: 0.4, pt: 0.4, borderTop: '1px dashed rgba(255,255,255,0.15)', color: 'text.secondary', fontSize: '0.68rem', display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.2 }}>
                                <Box component="span" sx={{ whiteSpace: 'nowrap', color: '#22c55e' }}>
                                  Доступно к выводу: <strong>{formatNumber(fs.free_balance ?? 0, 0)}</strong>
                                </Box>
                                {(fs.staked_locked ?? 0) > 0 && (
                                  <Box component="span" sx={{ whiteSpace: 'nowrap', color: '#f59e0b' }}>
                                    В стейке тело (заблок.): <strong>{formatNumber(fs.staked_locked ?? 0, 0)}</strong>
                                  </Box>
                                )}
                                <Box component="span" sx={{ whiteSpace: 'nowrap', color: (fs.slot_net_engine ?? 0) >= 0 ? '#ef4444' : '#22c55e' }}>
                                  Слоты (движок): {(fs.slot_net_engine ?? 0) >= 0 ? '+' : ''}{formatNumber(fs.slot_net_engine ?? 0, 0)}
                                </Box>
                                <Box component="span" sx={{ whiteSpace: 'nowrap', color: '#3b82f6' }}>
                                  Стейк наэмитил: +{formatNumber(fs.staking_emitted ?? 0, 0)}
                                </Box>
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ 
                        maxWidth: 150, 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {withdrawal.wallet_address.slice(0, 8)}...
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(withdrawal.wallet_address)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Chip
                        label={getStatusLabel(withdrawal.status)}
                        color={getStatusColor(withdrawal.status)}
                        size="small"
                      />
                      {withdrawal.approval_reason && withdrawal.status === 'pending' && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                          🔐 {withdrawal.approval_reason.length > 30 ? withdrawal.approval_reason.slice(0, 30) + '...' : withdrawal.approval_reason}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 110, maxWidth: 110, p: 0.5 }}>
                    {withdrawal.transaction_hash ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                        <IconButton
                          size="small"
                          title={withdrawal.transaction_hash}
                          onClick={() => copyToClipboard(withdrawal.transaction_hash)}
                          sx={{ p: 0.5 }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => window.open(`https://tonscan.org/tx/${withdrawal.transaction_hash}`, '_blank')}
                          sx={{ p: 0.5 }}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(withdrawal.created_at).toLocaleString('ru-RU')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 152 }}>
                    {/* Индикатор статуса юзера: ЧС или WL (бейдж сверху, своей строкой) */}
                    {(withdrawal.user_blacklisted || withdrawal.user_whitelisted) && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 0.75 }}>
                        {withdrawal.user_blacklisted && (
                          <>
                            <Chip label="В ЧС" size="small" color="error" sx={{ fontWeight: 'bold' }} />
                            <IconButton color="success" onClick={() => handleUnblacklist(withdrawal)} title="Убрать из ЧС" sx={ACTION_BTN_SX}>
                              <LockOpen />
                            </IconButton>
                          </>
                        )}
                        {withdrawal.user_whitelisted && (
                          <>
                            <Chip label="WL" size="small" color="success" sx={{ fontWeight: 'bold' }} title={withdrawal.whitelist_reason || ''} />
                            <IconButton color="warning" onClick={() => handleRemoveFromWhitelist(withdrawal)} title="Убрать из WL" sx={ACTION_BTN_SX}>
                              <Close />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    )}
                    {withdrawal.user_blacklisted ? null : withdrawal.status === 'pending' ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                        <IconButton color="success" onClick={() => handleApprove(withdrawal)} title="Одобрить" sx={ACTION_BTN_SX}><Check /></IconButton>
                        <IconButton color="error" onClick={() => setRejectDialog(withdrawal)} title="Отклонить" sx={ACTION_BTN_SX}><Close /></IconButton>
                        <IconButton color="warning" onClick={() => setBlacklistDialog(withdrawal)} title="ЧС" sx={ACTION_BTN_SX}><Block /></IconButton>
                        {!withdrawal.user_whitelisted && (
                          <IconButton color="success" onClick={() => setWhitelistDialog(withdrawal)} title="в WL" sx={ACTION_BTN_SX}><Verified /></IconButton>
                        )}
                        <Tooltip title={withdrawal.silent_ignore ? 'Снять тихий игнор' : 'Тихий игнор'}>
                          <IconButton onClick={async () => {
                            try { await adminAPI.post('/silent-ignore', { userId: withdrawal.user_id, ignore: !withdrawal.silent_ignore }); loadWithdrawals(); } catch (e) { alert('Ошибка: ' + e.message); }
                          }} sx={{ ...ACTION_BTN_SX, color: withdrawal.silent_ignore ? '#ff9800' : '#888' }}>
                            {withdrawal.silent_ignore ? '🔇' : '🔈'}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {withdrawal.status === 'approved' ? 'Одобрено' :
                         withdrawal.status === 'rejected' ? 'Отклонено' :
                         'В обработке'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                {isProcessing && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 0, borderBottom: 'none' }}>
                      <Box sx={{ width: '100%', py: 1 }}>
                        <LinearProgress />
                        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                          Отправка токенов на блокчейн... (~10 секунд)
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>);
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={100}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Строк на странице:"
        />
      </Paper>

      {/* Диалог деталей */}
      <Dialog 
        open={!!detailsDialog} 
        onClose={() => setDetailsDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailsDialog && (
          <>
            <DialogTitle>
              Детали вывода #{detailsDialog.id}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Пользователь
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {detailsDialog.first_name || 'Пользователь'}
                  </Typography>
                  {detailsDialog.telegram_username && (
                    <Typography variant="caption" color="text.secondary">
                      {detailsDialog.telegram_username}
                    </Typography>
                  )}
                  {!detailsDialog.telegram_username && (
                    <Typography variant="caption" color="text.secondary">
                      ID: {detailsDialog.user_id}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Статус
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={getStatusLabel(detailsDialog.status)}
                      color={getStatusColor(detailsDialog.status)}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Сумма к выводу
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(detailsDialog.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Адрес кошелька
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {detailsDialog.wallet_address}
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={() => copyToClipboard(detailsDialog.wallet_address)}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
                {detailsDialog.transaction_hash && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Хеш транзакции
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {detailsDialog.transaction_hash}
                      </Typography>
                      <IconButton 
                        size="small"
                        onClick={() => window.open(`https://tonscan.org/tx/${detailsDialog.transaction_hash}`, '_blank')}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                )}
                {detailsDialog.approval_reason && detailsDialog.status === 'pending' && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      <Typography variant="body2">
                        <strong>🔐 Требует подтверждения:</strong> {detailsDialog.approval_reason}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                {detailsDialog.reject_reason && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="body2">
                        <strong>Причина отклонения:</strong> {detailsDialog.reject_reason}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Создано
                  </Typography>
                  <Typography variant="body2">
                    {new Date(detailsDialog.created_at).toLocaleString('ru-RU')}
                  </Typography>
                </Grid>
                {detailsDialog.processed_at && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Обработано
                    </Typography>
                    <Typography variant="body2">
                      {new Date(detailsDialog.processed_at).toLocaleString('ru-RU')}
                    </Typography>
                  </Grid>
                )}
                {detailsDialog.admin_username && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Обработал администратор
                    </Typography>
                    <Typography variant="body2">
                      {detailsDialog.admin_username}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {detailsDialog.status === 'pending' && (
                <>
                  <Button 
                    onClick={() => setRejectDialog(detailsDialog)}
                    color="error"
                    startIcon={<Close />}
                  >
                    Отклонить
                  </Button>
                  <Button 
                    onClick={() => handleApprove(detailsDialog)}
                    color="success"
                    variant="contained"
                    startIcon={<Check />}
                  >
                    Одобрить
                  </Button>
                </>
              )}
              {detailsDialog.status === 'approved' && (
                <Button 
                  onClick={() => handleProcess(detailsDialog)}
                  color="primary"
                  variant="contained"
                  startIcon={<AttachMoney />}
                >
                  Отправить средства
                </Button>
              )}
              <Button onClick={() => setDetailsDialog(null)}>
                Закрыть
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Диалог отклонения */}
      <Dialog 
        open={!!rejectDialog} 
        onClose={() => {
          setRejectDialog(null);
          setRejectReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Отклонение вывода
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Средства будут возвращены на баланс пользователя
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Причина отклонения"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Укажите причину отклонения заявки..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialog(null);
            setRejectReason('');
          }}>
            Отмена
          </Button>
          <Button 
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectReason.trim()}
          >
            Отклонить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления в ЧС */}
      <Dialog
        open={!!blacklistDialog}
        onClose={() => {
          setBlacklistDialog(null);
          setBlacklistReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Добавить в черный список
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Пользователь не сможет создавать заявки на вывод!</strong><br/>
            Текущая заявка будет отклонена, средства возвращены на баланс.
          </Alert>
          {blacklistDialog && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1, border: '1px solid rgba(255,255,255,0.1)' }}>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <strong>Пользователь:</strong> {blacklistDialog.first_name || blacklistDialog.username || 'ID: ' + blacklistDialog.user_id}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <strong>Сумма заявки:</strong> {formatNumber(blacklistDialog.amount)} {blacklistDialog.currency || 'SPERM'}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Причина (необязательно)"
            value={blacklistReason}
            onChange={(e) => setBlacklistReason(e.target.value)}
            placeholder="Укажите причину добавления в ЧС..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBlacklistDialog(null);
            setBlacklistReason('');
          }}>
            Отмена
          </Button>
          <Button
            onClick={handleBlacklist}
            color="error"
            variant="contained"
            startIcon={<Block />}
          >
            Добавить в ЧС
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления в whitelist */}
      <Dialog
        open={!!whitelistDialog}
        onClose={() => {
          setWhitelistDialog(null);
          setWhitelistReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'success.main' }}>
          Добавить в whitelist выводов
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Доверенный юзер.</strong><br/>
            Выводы пройдут без лимитов автоодобрения (max amount + дневной).
            2FA и проверка адреса работают как обычно. Если юзер был в ЧС — снимается автоматически.
          </Alert>
          {whitelistDialog && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1, border: '1px solid rgba(255,255,255,0.1)' }}>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <strong>Пользователь:</strong> {whitelistDialog.first_name || whitelistDialog.username || 'ID: ' + whitelistDialog.user_id}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <strong>Сумма заявки:</strong> {formatNumber(whitelistDialog.amount)} {whitelistDialog.currency || 'SPERM'}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Причина (необязательно)"
            value={whitelistReason}
            onChange={(e) => setWhitelistReason(e.target.value)}
            placeholder="Например: проверенный VIP, длительная история выводов..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setWhitelistDialog(null);
            setWhitelistReason('');
          }}>
            Отмена
          </Button>
          <Button
            onClick={handleAddToWhitelist}
            color="success"
            variant="contained"
            startIcon={<Verified />}
          >
            Добавить в WL
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно с деталями пользователя */}
      <UserDetailsModal
        open={userDetailsModal.open}
        onClose={() => setUserDetailsModal({ open: false, userId: null })}
        userId={userDetailsModal.userId}
      />

      {/* P&L модалка */}
      <Dialog open={!!pnlModal} onClose={() => setPnlModal(null)} maxWidth="sm" fullWidth>
        {pnlModal && (() => {
          const dd = pnlModal;
          const pp = dd.profit || {};
          const prr = dd.prices || {};
          const ssa = pp.spermAssets || {};
          const uu = pp.usdt || {};
          const ww = dd.wallet || {};
          const mbb = dd.blockchain?.monthlyBuys || {};
          const tonU = prr.tonPriceUsd || 0;
          const rubR = prr.usdRub || 80;
          const spOnW = ssa.onWallet || ww.spermOnWallet || 0;
          const uOut = (uu.withdrawn || 0) + (uu.balance || 0);
          const uIn = uu.deposited || 0;
          // P&L по историческим ценам (как полный сканер)
          const tonInvested = pp.totalTonIn || 0;
          const usdFromMb = Object.values(mbb).reduce((s, m) => s + (m.usdSpent || 0), 0);
          const usdI = (usdFromMb > 0 ? usdFromMb : tonInvested * tonU) + uIn;
          const usdS = (dd.blockchain?.dexSells || []).reduce((s, sl) => s + (sl.usdReceived || sl.ton * tonU), 0);
          const usdSp = spOnW * (prr.spermPriceUsd || 0);
          const usdTon = (ww.tonOnWallet || pp.tonOnWallet || 0) * tonU;
          const usdN = usdS + usdSp + usdTon + uOut;
          const usdP = usdN - usdI;
          return (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                P&L @{dd.user?.username}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Курс: 1 SPERM = {fmtNum(prr.spermPriceInTon, 4)} TON | 1 TON = ${fmtNum(tonU, 2)} | $1 = {fmtNum(rubR, 0)}₽
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ textAlign: 'center', mb: 2, p: 2, bgcolor: usdP >= 0 ? 'rgba(76,175,80,0.1)' : 'rgba(244,67,54,0.1)', borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: usdP >= 0 ? '#4caf50' : '#f44336' }}>
                    {usdP >= 0 ? '+' : ''}${fmtNum(usdP)} (~{fmtNum(usdP * rubR)}₽)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Вложил ${fmtNum(usdI)} → На руках ${fmtNum(usdN)}
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={4}><Typography variant="body2">Вложил TON</Typography><Typography variant="h6" sx={{ color: '#f44336' }}>{fmtNum(pp.totalTonIn)}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="body2">SPERM на кош.</Typography><Typography variant="h6" sx={{ color: '#2196f3' }}>{fmtNum(spOnW)}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="body2">USDT</Typography><Typography variant="h6" sx={{ color: (uOut - uIn) >= 0 ? '#4caf50' : '#f44336' }}>{(uOut - uIn) >= 0 ? '+' : ''}{fmtNum(uOut - uIn)}</Typography></Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">На кошельке: <b>{fmtNum(ww.tonOnWallet || 0, 2)} TON</b> + <b>{fmtNum(spOnW)} SPERM</b></Typography>
                  {(pp.totalTonOut || 0) > 0 && <Typography variant="body2">Обменял в TON: <b>{fmtNum(pp.totalTonOut, 2)}</b></Typography>}
                  <Typography variant="body2">У нас в игре: <b>{fmtNum(ssa.inGame || 0)} SPERM</b> (~{fmtNum(ssa.inGameTon || 0)} TON)</Typography>
                  {dd.deposits?.byCurrency?.SPERM?.total > 0 && <Typography variant="body2" color="text.secondary">Депозитов SPERM: <b>{fmtNum(dd.deposits.byCurrency.SPERM.total)}</b> ({dd.deposits.byCurrency.SPERM.count})</Typography>}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button size="small" onClick={() => sendPnlReport(dd)} disabled={pnlReportSending}>
                  {pnlReportSending ? '...' : 'HTML в Telegram'}
                </Button>
                <Button size="small" onClick={() => { setPnlModal(null); window.open(`/wallet-scanner?tab=2&user=${dd.user?.username}`, '_blank'); }}>
                  Полный отчёт
                </Button>
                <Button onClick={() => setPnlModal(null)}>Закрыть</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default Withdrawals;